import type { AssetVitalityCounts } from '../../../client/IvtHubClient';

/**
 * One industry row's asset-vitality bar, with the three counts underneath it.
 *
 * ⭐ The scale is passed IN, not computed here. Every row shares one px-per-asset derived
 * from the widest row, so bar lengths are comparable BETWEEN rows — computing a scale per
 * row would make every bar the same length and throw that away. (Measured off the Figma
 * frame: 4.26–4.71 px per asset across the seven rows, with total widths from 98 to 181px.
 * Equal totals would have meant per-row normalisation.)
 *
 * ⚠️ Length encodes DRAWN UNITS, not asset count: an empty band still occupies one asset's
 * width so the counts have something to align to, so a row with empty bands draws longer
 * than its assets alone justify (an 8-asset row with no empty band can be shorter than a
 * 6-asset row with two). That is the accepted cost of the empty-band slot, and it is why
 * {@link vitalityUnits} — not the plain asset total — is what the scale must be built from.
 *
 * Not to be confused with the KPI band's vitality pills, which fill a fixed track because
 * they are a single row split three ways. See scale.ts.
 */

const BANDS = [
  { key: 'green', className: 'is-green' },
  { key: 'amber', className: 'is-amber' },
  { key: 'red', className: 'is-red' },
] as const;

/**
 * Narrowest band that can host its own count without the number leaving the band.
 *
 * ⚠️ NOT "wide enough for two digits" — two 12px Amazon Ember digits are about 14.4px, so a
 * band at exactly this width can hold one digit comfortably and two only just. The value is
 * chosen against the LIVE SCALE, not against the glyphs: beta's widest row gives 16.4px per
 * asset, and at the previous 18 every row containing a 0 or a 1 fell to the inline layout
 * while the rest stayed per-band, so one table showed both. 14 sits below 16.4 with 2.4px of
 * margin. ⇒ If the data grows enough to push px(1) under 14, the whole table flips to inline
 * together, which is the intended degradation — see labelLayoutFor.
 */
const MIN_LABEL_HOST_PX = 14;

/**
 * Which label layout the WHOLE TABLE uses. Decided once, from the shared scale.
 *
 * ⭐ This used to be decided per row, and that was the defect: the deciding quantity is
 * `px(1)` — the width of a single asset, and of an empty band's slot — which is a property
 * of the table-wide scale, not of a row. Judging it per row meant a table could render some
 * rows with counts pinned under their bands and others with counts run together inline,
 * which reads as broken alignment rather than as two deliberate layouts.
 *
 * Any row's narrowest band is at least `px(1)` (a 1-count band, or an empty band's slot), so
 * this single test is exactly the per-row test applied to the worst case.
 */
export function labelLayoutFor(px: (value: number) => number): 'per-band' | 'inline' {
  return px(1) >= MIN_LABEL_HOST_PX ? 'per-band' : 'inline';
}

/**
 * Gap between adjacent bands. Measured on the frame: 2px, on 6 of the 7 rows (the seventh
 * reads 3px, rounding noise from the group's transform).
 *
 * Applied inline rather than from the stylesheet on purpose — the label offsets below step
 * over it, so the constant has to be the single definition. A CSS-only `gap` would drift
 * out of sync with the offsets and put every count out of line again.
 */
const SEG_GAP_PX = 2;

/**
 * Drawn units for one row: its assets, plus one unit for each EMPTY band.
 *
 * This is what a bar's width is proportional to, so it is what the shared scale's basis has
 * to be the maximum of. Using the plain asset total instead lets the widest row overflow its
 * track by one unit per empty band — which is how the column ends up wider than the space
 * reserved for it, and Cloudscape then clips the tail.
 */
export function vitalityUnits(v: AssetVitalityCounts | undefined): number {
  return BANDS.reduce((sum, b) => {
    const c = v?.[b.key] ?? 0;
    return sum + (Number.isFinite(c) && c > 0 ? c : 1);
  }, 0);
}

interface Props {
  vitality: AssetVitalityCounts | undefined;
  /** Shared cross-row scale; see {@link linearScale} in scale.ts. */
  px: (value: number) => number;
  /** Decided once for the table by {@link labelLayoutFor} — never per row. */
  layout: 'per-band' | 'inline';
}

export function VitalityBar({ vitality, px, layout }: Props) {
  const counts = BANDS.map((b) => {
    const c = vitality?.[b.key] ?? 0;
    return Number.isFinite(c) && c > 0 ? c : 0;
  });

  /**
   * An empty band gets a slot exactly as wide as ONE asset on this scale — never wider. A
   * fixed-width placeholder was the obvious first idea and it inverts the encoding: as soon
   * as some industry has enough assets to shrink the scale, "none" would render WIDER than
   * "one". Pegging it to px(1) makes zero and one the same width, told apart by colour
   * rather than by size, which cannot invert.
   */
  const widths = counts.map((c) => (c > 0 ? px(c) : px(1)));
  const barWidth = widths.reduce((a, b) => a + b, 0) + SEG_GAP_PX * (BANDS.length - 1);

  /**
   * ⭐ Per-band positioning needs every band wide enough to hold its own number; the inline
   * fallback is for when they are not. The choice is the TABLE's (see labelLayoutFor), so it
   * arrives as a prop — deciding it here, per row, is what put two layouts in one table.
   *
   * Do not replace the fallback with a nudge. The version before it pushed a crowded label
   * right by a minimum step with NO upper bound, and that is strictly worse than crowding:
   * at a 39-asset basis a `{1, 1, 37}` row drew the amber "1" 6.8px INSIDE the red band, so
   * amber's band showed no number and red's showed two, and a reader attributes the wrong
   * count to the wrong band. In the fallback the counts keep band ORDER and band COLOUR, so
   * the attribution survives even though the horizontal alignment does not.
   */
  const perBand = layout === 'per-band';

  let cursor = 0;
  const offsets = widths.map((w) => {
    const start = cursor;
    cursor += w + SEG_GAP_PX;
    return start;
  });

  return (
    <div className="ivt-dashboard__vbar">
      <div className="ivt-dashboard__vbar-track" style={{ gap: SEG_GAP_PX }}>
        {/* An empty band keeps its OWN colour class and adds `is-empty`, which desaturates
            it in CSS. So the slot is a washed-out version of that band rather than a
            neutral grey — you can still tell WHICH band is empty — and the three hexes stay
            defined in one place. */}
        {BANDS.map((band, i) => (
          <div
            key={band.key}
            className={`ivt-dashboard__vbar-seg ${band.className}${counts[i] > 0 ? '' : ' is-empty'}`}
            style={{ width: widths[i] }}
          />
        ))}
      </div>

      {perBand ? (
        /* Absolutely positioned rather than in flow: a number wider than its own band would
           otherwise shift every number after it, which is how the counts first drifted out
           of line with their bands. */
        <div className="ivt-dashboard__vbar-labels" style={{ width: barWidth }}>
          {BANDS.map((band, i) => (
            <span
              key={band.key}
              className={`ivt-dashboard__vbar-label ${band.className}`}
              style={{ left: offsets[i] }}
            >
              {counts[i]}
            </span>
          ))}
        </div>
      ) : (
        /* Separated by "/" like the column header's 活跃 / 低活 / 停摆, so three numbers in a
           row read as three bands rather than as one figure. */
        <div className="ivt-dashboard__vbar-labels is-inline">
          {BANDS.map((band, i) => (
            <span key={band.key} className="ivt-dashboard__vbar-inline-item">
              {i > 0 && <span className="ivt-dashboard__vbar-sep">/</span>}
              <span className={`ivt-dashboard__vbar-label ${band.className}`}>{counts[i]}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
